import React, { useMemo, useState } from 'react';
import {
	Flex,
	Grid,
	Input,
	Table,
} from 'antd';
import { CargoContractStatusTypes, EstimateStatus } from '@shared/utils/constants';
import { GetCargosResponse } from '@api/features/cargos/getCargos';
import { CargoDetails } from '@api/features/cargos/getCargoDetails';
import showSuccessNotification from '@client/utils/showSuccessNotification';
import showErrorNotification from '@client/utils/showErrorNotification';
import { getCargos } from '@client/lib/api';
import Button from '@client/components/Button';
import { useTableQuery } from '@client/utils/hooks/useTableQuery';
import { useAuth } from '@client/lib/auth';
import Modal from '@client/components/Modal/Modal';
import { useCargoProvider } from '../context/CargoContext';
import getImportCargoColumns from '../helpers/importCargoColumns';
import { useCentralStore } from '../context/CentralStoreContext';

export type FixedByOption = {
	fixedByName: string;
};

const allowedContractStatuses = [
	CargoContractStatusTypes.Pending,
	CargoContractStatusTypes.TentativeBooking,
	CargoContractStatusTypes.Contracted,
];

const ImportFromCargoBookModal = ({
	open,
	onClose,
	copyIntoBlankEstimate = false,
}: {
	open: boolean;
	onClose: () => void;
	copyIntoBlankEstimate?: boolean;
}) => {
	const screens = Grid.useBreakpoint();
	const { userOptions } = useAuth();

	const {
		estimate,
		onImportCargoMutator,
		chartererOptions,
	} = useCargoProvider();

	const {
		data: cargos,
		isLoading,
		pagination,
		setSearch,
		onTableChange,
	} = useTableQuery<GetCargosResponse>({
		queryKey: ['cargos', 'importable'],
		fetcher: ({ pagination, filters, sorter, search }) =>
			getCargos({
				pagination,
				sorter,
				search,
				filters: {
					...filters,
					contractStatus: allowedContractStatuses,
				},
				includeGhostCargos: true,
			}),
		defaultPageSize: 100,
	});

	const { handleCreateSubEstimate } = useCentralStore();

	const [selectedCargos, setSelectedCargos] = useState<CargoDetails[]>([]);
	const selectedCargoIds = selectedCargos.map((cargo) => cargo.id);
	const cargosOnEstimate = estimate?.cargos ?? [];
	const isOnEstimate = (cargoId: number) => cargosOnEstimate.some((c) => c.id === cargoId);

	const getModalWidth = () => {
		if (screens.xxl) {
			return '85%';
		}

		return '95%';
	};

	const fixedByOptions: FixedByOption[] = useMemo(() => {
		return (userOptions ?? [])
			.flatMap((user) => (
				user.label != null ?
					[{ fixedByName: user.label }] :
					[]
			));
	}, [userOptions]);

	const handleClose = () => {
		setSelectedCargos([]);
		onClose();
	};

	const handleImportCargos = async (cargoIds: number[]) => {
		if (copyIntoBlankEstimate) {
			try {
				await handleCreateSubEstimate({ blank: true, cargoIds });
				showSuccessNotification(cargoIds.length > 1 ?
					'Blank estimate created with cargoes' :
					'Blank estimate created with cargo');
				handleClose();
			} catch (e) {
				showErrorNotification('Failed to create blank estimate with cargo', e as Error);
			}
		} else {
			onImportCargoMutator.mutate(cargoIds, {
				onSuccess: () => {
					showSuccessNotification(cargoIds.length > 1 ? 'Cargoes added to estimate' : 'Cargo added to estimate');
					handleClose();
				},
				onError: (e: Error) => {
					showErrorNotification('Failed to add cargo to estimate', e as Error);
				},
			});
		}
	};

	const selectedCargosInUse = selectedCargos.some((cargo) => (
		cargo.status != null && cargo.status !== EstimateStatus.Available
	));

	const columns = getImportCargoColumns({
		onImportCargo: (cargoId) => handleImportCargos([cargoId]),
		cargosOnEstimate,
		chartererOptions,
		fixedByOptions,
	});

	return (
		<Modal
			title="Add existing cargo"
			open={open}
			width={getModalWidth()}
			onCancel={handleClose}
			footer={false}
			style={{ maxWidth: '100vw' }}
			styles={{ body: { overflowX: 'auto' } }}
			centered
		>
			<div style={{ width: '100%', overflowX: 'auto' }}>
				<Flex justify="space-between" align="center" gap={8} style={{ marginBottom: 16 }}>
					<Input.Search
						placeholder="Search cargos"
						onSearch={(value) => setSearch(value)}
						style={{ maxWidth: 400 }}
					/>
					<Button
						type="primary"
						disabled={selectedCargoIds.length === 0}
						confirmTitle={selectedCargosInUse ?
							'Some of the selected cargoes are on other estimates or contracts. ' +
							'Are you sure you want to add them to this estimate?' :
							null}
						onClick={() => handleImportCargos(selectedCargoIds)}
						data-testid="import-selected-cargoes"
					>
						{`Add selected cargoes (${selectedCargoIds.length})`}
					</Button>
				</Flex>
				<Table<CargoDetails>
					rowKey="id"
					rowSelection={{
						selectedRowKeys: selectedCargoIds,
						preserveSelectedRowKeys: true,
						onChange: (_keys, rows) => setSelectedCargos(rows),
						getCheckboxProps: (row) => ({ disabled: isOnEstimate(row.id) }),
					}}
					columns={columns}
					dataSource={cargos?.rows ?? []}
					pagination={{
						current: pagination.current,
						pageSize: pagination.pageSize,
						total: cargos?.count ?? 0,
						pageSizeOptions: [50, 100, 200, 500],
					}}
					onChange={onTableChange}
					loading={isLoading}
					scroll={{ x: 1350 }}
					size="small"
				/>
			</div>
		</Modal>
	);
};

export default ImportFromCargoBookModal;
